import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileHeader from './MobileHeader';
import MobileBottomNav from './MobileBottomNav';
import { Toaster } from '../ui/sonner';
import { useAuth } from '../../contexts/AuthContext';
import TrialExpiredOverlay from '../TrialExpiredOverlay';
import SetupWizard from '../SetupWizard';
import PortalSelector from '../PortalSelector';
import { X } from 'lucide-react';

const MainLayout = () => {
  const navigate = useNavigate();
  const [showManualWizard, setShowManualWizard] = useState(false);
  
  const { 
    user, 
    logout, 
    isTrialExpired, 
    showOnboarding, 
    completeOnboarding, 
    skipOnboarding,
    companyInfo,
    showPortalSelector,
    selectPortal,
    activePortal
  } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePortalSelect = (portal) => {
    selectPortal(portal);
    if (portal === 'admin') {
      navigate('/admin');
    } else if (portal === 'travaux') {
      navigate('/travaux');
    } else {
      navigate('/agent');
    }
  };

  const handleOpenGuide = () => {
    setShowManualWizard(true);
  };

  const handleCloseGuide = () => {
    setShowManualWizard(false);
  };

  // Show trial expired overlay for admins
  const showExpiredOverlay = user?.role === 'admin' && isTrialExpired();
  
  // Portal selector has priority over onboarding
  const shouldShowPortalSelector = showPortalSelector && !showExpiredOverlay;
  
  // Only show onboarding if:
  // 1. Portal is already selected (or user has single role)
  // 2. User is admin AND viewing admin portal (not agent portal)
  // 3. Not showing portal selector
  const isViewingAdminPortal = user?.role === 'admin' && activePortal !== 'agent';
  const shouldShowOnboarding = (showOnboarding || showManualWizard) && isViewingAdminPortal && !showExpiredOverlay && !shouldShowPortalSelector;

  // Show guide button only for admins viewing admin portal
  const showGuideButton = isViewingAdminPortal && !showExpiredOverlay && !shouldShowPortalSelector && !shouldShowOnboarding;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Sidebar onOpenGuide={showGuideButton ? handleOpenGuide : null} />
      <MobileHeader />
      <MobileBottomNav />
      <div className="main-content">
        <Outlet />
      </div>
      <Toaster position="top-right" />
      
      {/* Trial Expired Overlay */}
      {showExpiredOverlay && (
        <TrialExpiredOverlay 
          companyName={companyInfo?.nom_entreprise}
          onLogout={handleLogout}
        />
      )}
      
      {/* Portal Selector for dual-role users - HAS PRIORITY */}
      {shouldShowPortalSelector && (
        <PortalSelector
          companyName={companyInfo?.nom_entreprise}
          onSelect={handlePortalSelect}
        />
      )}
      
      {/* Setup Wizard for new admins - after portal selection */}
      {shouldShowOnboarding && (
        <SetupWizard
          companyName={companyInfo?.nom_entreprise}
          onComplete={() => {
            completeOnboarding();
            setShowManualWizard(false);
          }}
          onSkip={() => {
            skipOnboarding();
            setShowManualWizard(false);
          }}
          isManualOpen={showManualWizard}
        />
      )}
    </div>
  );
};

export default MainLayout;
